import { AnalyticsContext, useTranslation } from '@shared/core';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useCallback, useContext } from 'react';
import { usePrimaryEventLocation } from '../routes/Dashboard/components/ProductDetailsPage/usePrimaryEventLocation';
import { fillPageWithEventInfo } from '../utils/fillPageWithEventInfo';
import { ThemeJson } from '../routes/CardCustomizer/CardCustomizer.types';

export type TemplateForAutofill = { id: string; filledAt?: string; themeJSON?: ThemeJson };

// Gallery autofill will opt out of telemetry due to the large volume.
export function useAutofill(trackEvent = true) {
  const { track } = useContext(AnalyticsContext);
  const { t } = useTranslation('stationery');
  const tNumerals = t('numerals');

  const [isAutofillEnabled, featureLoading] = useIsFeatureEnabled('printGalleryAutofill');
  const { eventHandle, eventInfo, loading: eventInfoLoading } = useEventInfo();
  const { location, loading: locationLoading } = usePrimaryEventLocation();
  const loading = featureLoading || eventInfoLoading || locationLoading;

  return [
    useCallback(
      (template: TemplateForAutofill) => {
        if (!isAutofillEnabled) return;
        if (!template) return;
        if (!template.themeJSON) return;
        if (loading) return;

        const { front, back } = template.themeJSON.card;
        // prettier-ignore
        const filledPatterns = [
          ...fillPageWithEventInfo(front, eventInfo, location, tNumerals),
          ...fillPageWithEventInfo(back, eventInfo, location, tNumerals)
        ];
        template.themeJSON.filledAt = new Date().toISOString();
        if (trackEvent) {
          track({
            category: 'messaging.card',
            action: 'Patterns Filled',
            extraInfo: {
              templateId: template.id,
              eventHandle,
              eventId: eventInfo?.eventId,
              filledPatterns,
            },
          });
        }
      },
      [eventHandle, eventInfo, isAutofillEnabled, location, loading, tNumerals, track, trackEvent],
    ),
    loading,
  ] as const;
}
